import { useMemo, useState } from 'react';
import { ChevronLeft, Heart, LayoutGrid, List, Search } from 'lucide-react';
import FloorPlanSegmentedToggle from '../../../components/FloorPlanSegmentedToggle.jsx';
import {
  formatOpeningDimensions,
  getOpeningCatalogForMode,
} from '../../../lib/floorPlanOpeningCatalog.js';
import OpeningCatalogPreview from './OpeningCatalogPreview.jsx';

/**
 * @param {{
 *   mode: 'doors' | 'windows' | 'structures';
 *   onBack: () => void;
 *   onSelectItem: (item: import('../../../lib/floorPlanOpeningCatalog.js').FloorPlanOpeningCatalogItem) => void;
 * }} props
 */
function AssetLibraryPanel({ mode, onBack, onSelectItem }) {
  const [activeTab, setActiveTab] = useState(mode === 'windows' ? 'windows' : 'doors');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewMode, setPreviewMode] = useState('3d');
  const [layoutMode, setLayoutMode] = useState('grid');

  const catalogMode = activeTab === 'windows' ? 'windows' : 'doors';
  const items = useMemo(() => {
    if (mode === 'structures') return [];
    const source = getOpeningCatalogForMode(catalogMode);
    const query = searchQuery.trim().toLowerCase();
    if (!query) return source;
    return source.filter((item) => item.name.toLowerCase().includes(query));
  }, [mode, catalogMode, searchQuery]);

  const title = mode === 'structures' ? 'Structures' : activeTab === 'windows' ? 'Windows' : 'Doors';

  return (
    <div className="floor-plan-workspace-library">
      <button type="button" className="floor-plan-workspace-library-back card" onClick={onBack}>
        <ChevronLeft size={18} aria-hidden />
        <span>back</span>
      </button>

      <aside className="floor-plan-workspace-library-panel card" aria-label={title}>
        <header className="floor-plan-workspace-library-head">
          <h2 className="floor-plan-workspace-panel-title">{title}</h2>
          {mode !== 'structures' ? (
            <FloorPlanSegmentedToggle
              value={activeTab}
              options={[
                { value: 'doors', label: 'Doors' },
                { value: 'windows', label: 'Windows' },
              ]}
              onChange={(next) => setActiveTab(String(next))}
              ariaLabel="Doors and windows"
              className="floor-plan-workspace-library-tabs"
            />
          ) : null}
        </header>

        {mode !== 'structures' ? (
          <>
            <div className="floor-plan-workspace-library-filters">
              <button type="button" className="floor-plan-workspace-library-fav btn btn-ghost" aria-label="Favorites">
                <Heart size={16} aria-hidden />
              </button>
              <label className="floor-plan-workspace-library-search">
                <Search size={16} aria-hidden />
                <input
                  type="search"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search library"
                />
              </label>
            </div>
            <label className="floor-plan-workspace-library-category">
              <span>All categories</span>
              <select disabled aria-label="Category filter">
                <option>All categories</option>
              </select>
            </label>
            <div className="floor-plan-workspace-library-meta">
              <span>{items.length} results</span>
              <div className="floor-plan-workspace-library-meta-actions">
                <button
                  type="button"
                  className={`floor-plan-workspace-library-layout-btn${layoutMode === 'grid' ? ' floor-plan-workspace-library-layout-btn--active' : ''}`}
                  onClick={() => setLayoutMode('grid')}
                  aria-label="Grid view"
                >
                  <LayoutGrid size={16} aria-hidden />
                </button>
                <button
                  type="button"
                  className={`floor-plan-workspace-library-layout-btn${layoutMode === 'list' ? ' floor-plan-workspace-library-layout-btn--active' : ''}`}
                  onClick={() => setLayoutMode('list')}
                  aria-label="List view"
                >
                  <List size={16} aria-hidden />
                </button>
                <FloorPlanSegmentedToggle
                  value={previewMode}
                  options={[
                    { value: '2d', label: '2D' },
                    { value: '3d', label: '3D' },
                  ]}
                  onChange={(next) => setPreviewMode(String(next))}
                  ariaLabel="Preview mode"
                  className="floor-plan-workspace-library-preview-toggle"
                />
              </div>
            </div>
          </>
        ) : null}

        <div
          className={`floor-plan-workspace-library-items floor-plan-workspace-library-items--${layoutMode}`}
        >
          {mode === 'structures' || items.length === 0 ? (
            <div className="floor-plan-workspace-empty-state">
              <p className="floor-plan-workspace-empty-title">
                {mode === 'structures' ? 'Structure library coming soon.' : 'No items found.'}
              </p>
              {mode === 'structures' ? (
                <p className="floor-plan-workspace-empty-copy">
                  Structural elements will appear here when the asset library is ready.
                </p>
              ) : null}
            </div>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="floor-plan-workspace-library-card"
                onClick={() => onSelectItem(item)}
              >
                <div className="floor-plan-workspace-library-card-preview">
                  <OpeningCatalogPreview
                    previewId={item.preview2D}
                    view={previewMode === '3d' ? '3d' : '2d'}
                  />
                </div>
                <span className="floor-plan-workspace-library-card-name">{item.name}</span>
                <span className="floor-plan-workspace-library-card-dims">{formatOpeningDimensions(item)}</span>
              </button>
            ))
          )}
        </div>
      </aside>
    </div>
  );
}

export default AssetLibraryPanel;
